import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

// Chart colours, by the job they do. The three series slots were validated
// as a set for colour-blind separation and contrast against the dark card
// surface (dataviz validate_palette.js, dark mode) — keep the order, and do
// not add a fourth series without re-validating. Positive/negative is a
// diverging pair (blue / red), only ever used for a value with a sign.
export const SERIES = ['#3987e5', '#d95926', '#199e70'] as const;
export const POSITIVE = '#3987e5';
export const NEGATIVE = '#e66767';
const GRID = '#1e293b'; // slate-800: a hairline one step off the card
const AXIS_TEXT = '#64748b'; // slate-500

export type BarSegment = { name: string; value: number; color: string };
export type BarColumn = {
  key: string;
  /** Tick label under the column. */
  label: string;
  segments: BarSegment[];
  /** Tooltip body for this column. */
  tooltip: ReactNode;
};

// A gridline step people read easily: 1, 2 or 5 × 10ⁿ. Ticks sit on
// multiples of it, so zero is always a gridline and a count axis never shows
// 12.5 customers.
function niceStep(raw: number, integer: boolean): number {
  if (!(raw > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 5, 10].find((candidate) => candidate * power >= raw) ?? 10) * power;
  return integer ? Math.max(1, Math.round(step)) : step;
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    setWidth(ref.current.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

// A rect with only its data end rounded (4px), so bars sit square on the
// baseline and the rounding marks the value. `up` false rounds the bottom,
// for a negative bar hanging below zero.
function barPath(x: number, y: number, w: number, h: number, up: boolean, round: boolean) {
  if (h <= 0 || w <= 0) return '';
  const r = round ? Math.min(4, w / 2, h) : 0;
  if (up) {
    return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
  }
  return `M${x},${y} V${y + h - r} Q${x},${y + h} ${x + r},${y + h} H${x + w - r} Q${x + w},${y + h} ${x + w},${y + h - r} V${y} Z`;
}

// Bars over a category axis — days, hours, weekdays. Segments stack; a single
// segment may be negative (net by day), drawn below a zero line. One y-axis,
// always. Hovering anywhere in a column shows its tooltip.
export function BarChart({
  columns,
  height = 220,
  formatValue,
  ariaLabel,
  integer = false,
}: {
  columns: BarColumn[];
  height?: number;
  formatValue: (value: number) => string;
  ariaLabel: string;
  /** Counts: keep gridlines on whole numbers. */
  integer?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const left = 56;
  const right = 8;
  const top = 8;
  const bottom = 24;
  const plotW = Math.max(width - left - right, 0);
  const plotH = height - top - bottom;

  const totals = columns.map((column) =>
    column.segments.reduce((sum, segment) => sum + Math.max(segment.value, 0), 0),
  );
  const lows = columns.map((column) =>
    column.segments.reduce((sum, segment) => sum + Math.min(segment.value, 0), 0),
  );
  const high = Math.max(0, ...totals);
  const low = Math.min(0, ...lows);
  const step = niceStep((high - low) / 5, integer);
  const max = Math.max(Math.ceil(high / step), 1) * step;
  const min = Math.floor(low / step) * step;
  const span = max - min || 1;
  const yOf = (value: number) => top + ((max - value) / span) * plotH;
  const zero = yOf(0);

  const ticks = Array.from({ length: Math.round(span / step) + 1 }, (_, index) => min + step * index);
  const slot = columns.length ? plotW / columns.length : 0;
  const barW = Math.max(Math.min(slot * 0.7, 40), 1);
  // At most ~10 tick labels, so a 90-day axis does not turn into a smear.
  const labelEvery = Math.max(1, Math.ceil(columns.length / Math.max(Math.floor(plotW / 56), 1)));

  const hovered = hover !== null ? columns[hover] : null;
  // Beside the hovered column, never on top of it, so the bar being read
  // stays visible; flips to the left side near the right edge.
  const flip = hover !== null && left + slot * (hover + 1) + 200 > width;
  const tooltipLeft =
    hover === null ? 0 : flip ? left + slot * hover - 8 : left + slot * (hover + 1) + 8;

  return (
    <div ref={ref} className="relative w-full" style={{ height }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={ariaLabel}
          onMouseLeave={() => setHover(null)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line x1={left} x2={width - right} y1={yOf(tick)} y2={yOf(tick)} stroke={GRID} strokeWidth={1} />
              <text
                x={left - 8}
                y={yOf(tick)}
                dy="0.32em"
                textAnchor="end"
                fontSize={11}
                fill={AXIS_TEXT}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatValue(tick)}
              </text>
            </g>
          ))}

          {columns.map((column, index) => {
            const x = left + slot * index + (slot - barW) / 2;
            let up = zero;
            const visible = column.segments.filter((segment) => segment.value !== 0);
            return (
              <g key={column.key}>
                {hover === index && (
                  <rect x={left + slot * index} y={top} width={slot} height={plotH} fill="#ffffff" opacity={0.04} />
                )}
                {visible.map((segment, segIndex) => {
                  if (segment.value < 0) {
                    const h = yOf(segment.value) - zero;
                    return <path key={segment.name} d={barPath(x, zero, barW, h, false, true)} fill={segment.color} />;
                  }
                  const h = zero - yOf(segment.value);
                  const isTop = segIndex === visible.length - 1 || visible.slice(segIndex + 1).every((s) => s.value <= 0);
                  // 2px of surface between stacked segments, taken from the
                  // segment below so the stack total stays exact at the top.
                  const gap = isTop ? 0 : Math.min(2, h / 2);
                  const y = up - h;
                  up = y;
                  return (
                    <path
                      key={segment.name}
                      d={barPath(x, y + gap, barW, h - gap, true, isTop)}
                      fill={segment.color}
                    />
                  );
                })}
                {index % labelEvery === 0 && (
                  <text
                    x={left + slot * (index + 0.5)}
                    y={height - 6}
                    textAnchor="middle"
                    fontSize={11}
                    fill={AXIS_TEXT}
                  >
                    {column.label}
                  </text>
                )}
                {/* Hit target: the whole column, much bigger than the bar. */}
                <rect
                  x={left + slot * index}
                  y={top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHover(index)}
                  onClick={() => setHover(index)}
                />
              </g>
            );
          })}

          {min < 0 && <line x1={left} x2={width - right} y1={zero} y2={zero} stroke={AXIS_TEXT} strokeWidth={1} />}
        </svg>
      )}

      {hovered && (
        <div
          className={`pointer-events-none absolute top-2 z-10 min-w-40 whitespace-nowrap rounded-lg border border-slate-700 bg-slate-950/95 px-3 py-2 text-xs text-slate-300 shadow-lg ${
            flip ? '-translate-x-full' : ''
          }`}
          style={{ left: tooltipLeft }}
        >
          {hovered.tooltip}
        </div>
      )}
    </div>
  );
}

// Identity is never colour alone: every multi-series chart carries this.
export function Legend({ items }: { items: { name: string; color: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
      {items.map((item) => (
        <span key={item.name} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: item.color }} />
          {item.name}
        </span>
      ))}
    </div>
  );
}

// A tooltip row: swatch, name, value — text in ink, the swatch carries identity.
export function TipRow({ color, name, value }: { color?: string; name: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="inline-flex items-center gap-1.5">
        {color && <span className="h-2 w-2 rounded-sm" style={{ background: color }} />}
        {name}
      </span>
      <span className="font-medium tabular-nums text-slate-100">{value}</span>
    </div>
  );
}

// Ranked horizontal bars for a short list of named amounts (expense
// categories). One series, so one colour; the number sits beside each bar.
export function RankedBars({
  rows,
  formatValue,
  color = SERIES[0],
}: {
  rows: { name: string; value: number; note?: string }[];
  formatValue: (value: number) => string;
  color?: string;
}) {
  const max = Math.max(...rows.map((row) => row.value), 1);
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.name} className="text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-slate-300">{row.name}</span>
            <span className="shrink-0 tabular-nums text-slate-100">
              {formatValue(row.value)}
              {row.note && <span className="ml-1.5 text-xs text-slate-500">{row.note}</span>}
            </span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-slate-800/60">
            <div
              className="h-2 rounded-full"
              style={{ width: `${(row.value / max) * 100}%`, background: color }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
